/**
 * Right-button drag over a node pans the canvas, exactly like right-dragging
 * blank canvas; a right-click without movement still opens the node context
 * menu.
 *
 * Why this exists: React Flow's zoom filter rejects gestures that start on a
 * draggable node (draggable nodes carry the `nopan` class), so d3-zoom never
 * sees a right-button drag there — the only thing that fires is the node's
 * native `contextmenu`, which used to open the menu immediately and make
 * panning impossible with a two-button mouse (big HTML blocks made this
 * obvious). The pane solves the same collision by suppressing `contextmenu`
 * outright and synthesizing the menu from an unmoved gesture end; this hook
 * mirrors that arbitration for nodes with its own pointer gesture + `panBy`.
 *
 * Scope guards:
 * - Only `nopan` nodes — anything else (hand tool, locked nodes) is already
 *   panned by d3-zoom, and handling it here would double-pan.
 * - Live HTML blocks never reach this code: their iframe owns the pointer, so
 *   the gesture can't start on the wrapper.
 * - `contextmenu` is suppressed only around an active gesture, so the
 *   keyboard context-menu key still reaches React Flow's `onNodeContextMenu`.
 *
 * Event-order note: Linux fires `contextmenu` at pointerdown (before we know
 * whether this is a drag), mac/Windows after pointerup. Both are handled by
 * suppressing while the gesture is active plus one trailing event after it
 * ends, and opening the menu from the unmoved pointerup instead.
 */

import { useStoreApi } from "@xyflow/react";
import { useEffect, useRef } from "react";
import { useCanvasUi } from "../context/CanvasUiContext";

/** Same intent as d3-zoom's pan-vs-contextmenu split: any real movement is a
 *  pan; a sub-threshold wobble is a click. */
const MOVE_THRESHOLD_PX = 5;

export interface UseNodeRightClickPanParams {
  /** Right-button panning enabled — MUST match the ReactFlow `panOnDrag`
   *  gate (viewport locks, placement modes) with button 2 included. */
  enabled: boolean;
  /** Open the node context menu for an unmoved right-click (permission and
   *  placement-mode checks live in the callback). */
  openNodeContextMenu: (
    nodeId: string,
    clientX: number,
    clientY: number,
  ) => void;
}

export function useNodeRightClickPan({
  enabled,
  openNodeContextMenu,
}: UseNodeRightClickPanParams) {
  const { reactFlowWrapperRef } = useCanvasUi();
  // panBy is a store action (it is not exposed on the ReactFlowInstance).
  const storeApi = useStoreApi();

  const openNodeContextMenuRef = useRef(openNodeContextMenu);
  openNodeContextMenuRef.current = openNodeContextMenu;

  useEffect(() => {
    const wrapper = reactFlowWrapperRef.current;
    if (!enabled || !wrapper) return;

    let gesture: {
      nodeId: string;
      startX: number;
      startY: number;
      lastX: number;
      lastY: number;
      moved: boolean;
    } | null = null;
    /** Stays set through the one trailing `contextmenu` the browser fires
     *  after pointerup (mac/Windows ordering). */
    let suppressContextMenu = false;

    const endGesture = () => {
      gesture = null;
      window.removeEventListener("pointermove", onPointerMove, true);
      window.removeEventListener("pointerup", onPointerUp, true);
      window.removeEventListener("pointercancel", onPointerCancel, true);
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!gesture) return;
      if (!gesture.moved) {
        const dist = Math.hypot(
          e.clientX - gesture.startX,
          e.clientY - gesture.startY,
        );
        if (dist < MOVE_THRESHOLD_PX) return;
        gesture.moved = true;
        // Content sticks to the cursor: apply the full offset accumulated
        // while under the threshold.
        gesture.lastX = gesture.startX;
        gesture.lastY = gesture.startY;
      }
      const dx = e.clientX - gesture.lastX;
      const dy = e.clientY - gesture.lastY;
      gesture.lastX = e.clientX;
      gesture.lastY = e.clientY;
      void storeApi.getState().panBy({ x: dx, y: dy });
    };

    const onPointerUp = (e: PointerEvent) => {
      if (!gesture) return;
      const { nodeId, moved } = gesture;
      endGesture();
      // The trailing contextmenu (when the platform sends one) lands within
      // the same input sequence; don't let the suppression outlive it and eat
      // an unrelated later event (e.g. the keyboard context-menu key).
      window.setTimeout(() => {
        suppressContextMenu = false;
      }, 300);
      if (!moved) {
        openNodeContextMenuRef.current(nodeId, e.clientX, e.clientY);
      }
    };

    const onPointerCancel = () => {
      endGesture();
    };

    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== 2 || gesture) return;
      const target = e.target as Element | null;
      // Editable regions keep the NATIVE context menu (paste, spellcheck) —
      // e.g. a text note mid-edit.
      if (
        target instanceof HTMLElement &&
        (target.isContentEditable || target.closest("input, textarea") !== null)
      ) {
        return;
      }
      const nodeEl = target?.closest?.(".react-flow__node");
      // Not a node (pane pans itself via d3-zoom), or a node d3-zoom already
      // pans (no `nopan` class — hand tool / locked nodes).
      if (!nodeEl || !nodeEl.classList.contains("nopan")) return;
      const nodeId = nodeEl.getAttribute("data-id");
      if (!nodeId) return;

      gesture = {
        nodeId,
        startX: e.clientX,
        startY: e.clientY,
        lastX: e.clientX,
        lastY: e.clientY,
        moved: false,
      };
      suppressContextMenu = true;
      window.addEventListener("pointermove", onPointerMove, true);
      window.addEventListener("pointerup", onPointerUp, true);
      window.addEventListener("pointercancel", onPointerCancel, true);
    };

    const onContextMenu = (e: MouseEvent) => {
      if (!gesture && !suppressContextMenu) return;
      // Kill both the native menu and React Flow's onNodeContextMenu (React
      // listens at the app root, above this wrapper, so stopping propagation
      // here prevents the synthetic handler from double-opening the menu).
      e.preventDefault();
      e.stopPropagation();
      if (!gesture) suppressContextMenu = false;
    };

    wrapper.addEventListener("pointerdown", onPointerDown, true);
    wrapper.addEventListener("contextmenu", onContextMenu, true);
    return () => {
      wrapper.removeEventListener("pointerdown", onPointerDown, true);
      wrapper.removeEventListener("contextmenu", onContextMenu, true);
      endGesture();
    };
  }, [enabled, reactFlowWrapperRef, storeApi]);
}
